import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import i18n from '../i18n';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const Ctx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  const setToken = (token) => {
    if (token) { axios.defaults.headers.common['Authorization'] = `Bearer ${token}`; localStorage.setItem('troly_token', token); }
    else        { delete axios.defaults.headers.common['Authorization']; localStorage.removeItem('troly_token'); }
  };

  const fetchMe = useCallback(async () => {
    try   { const { data } = await axios.get(`${API}/api/auth/me`); setUser(data.user); }
    catch { setUser(null); setToken(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const t = localStorage.getItem('troly_token');
    if (t) { setToken(t); fetchMe(); }
    else   { setLoading(false); }
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/api/auth/login`, { email, password });
    setToken(data.token); setUser(data.user); return data.user;
  };

  const register = async (name, email, password, code, location, fbBindToken) => {
    // El idioma con el que está navegando (geo-IP o su elección en el selector)
    // viaja en el alta: así la cuenta nace con el idioma correcto para los
    // correos en vez de heredar el default del schema.
    const language = (i18n.language || '').slice(0, 2);
    const payload = { name, email, password, code, location, language };
    if (fbBindToken) payload.fbBindToken = fbBindToken;
    const { data } = await axios.post(`${API}/api/auth/register`, payload);
    setToken(data.token); setUser(data.user); return data.user;
  };

  // Devuelve { status: 'logged_in', token, user } o
  // { status: 'invite_sent', email, name, fbBindToken }.
  const facebookLogin = async (accessToken) => {
    const { data } = await axios.post(`${API}/api/auth/facebook`, { accessToken });
    if (data.status === 'logged_in') { setToken(data.token); setUser(data.user); }
    return data;
  };

  const logout = () => { setUser(null); setToken(null); };

  const updateMe = async (patch) => {
    const { data } = await axios.patch(`${API}/api/users/me`, patch);
    setUser(data.user);
    return data.user;
  };

  // Compresión client-side <300KB antes de subir. El backend genera las
  // variantes WebP definitivas (96px thumb / 400px hero) con Sharp.
  const uploadAvatar = async (file) => {
    let payload = file;
    if (file.size > 300 * 1024) {
      try {
        payload = await imageCompression(file, {
          maxSizeMB: 0.3,
          maxWidthOrHeight: 800, // suficiente para el hero (400) con margen
          useWebWorker: true,
          initialQuality: 0.82,
        });
      } catch { /* si falla la compresión, mandamos el original */ }
    }
    const fd = new FormData();
    fd.append('avatar', payload);
    const { data } = await axios.post(`${API}/api/users/me/avatar`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    setUser(data.user);
    return data.user;
  };

  const changePassword = (current, next) =>
    axios.post(`${API}/api/users/me/password`, { current, next }).then(r => r.data);

  const changeEmail = async (email, password) => {
    const { data } = await axios.post(`${API}/api/users/me/email`, { email, password });
    setUser(data.user);
    return data.user;
  };

  const deleteAccount = async (password) => {
    await axios.delete(`${API}/api/users/me`, { data: { password } });
    setUser(null);
    setToken(null);
  };

  const joinWaitlist = (email) => axios.post(`${API}/api/auth/waitlist`, { email }).then(r => r.data);

  const validateCode = (code) => axios.get(`${API}/api/auth/invite/${code}`).then(r => r.data);

  // Journey A: valida invitación de un solo uso O codigoPropio reusable.
  const validateInviteCode = (code) =>
    axios.post(`${API}/api/auth/validate-code`, { code }).then(r => r.data);

  // Journey A: arranca el trial de 14 días en el primer login post-registro.
  const startTrial = async () => {
    const { data } = await axios.post(`${API}/api/user/start-trial`);
    if (data.user) setUser(data.user);
    return data;
  };

  // Journey A: estado del programa de referidos (membresía Pro gratis).
  const getReferralStatus = () =>
    axios.get(`${API}/api/user/referral-status`).then(r => r.data);

  // Journey A: comparte el código reusable de referido por correo.
  const sendReferralInvite = (email) =>
    axios.post(`${API}/api/user/referral-invite`, { email }).then(r => r.data);

  // ── Announcements / Rifa ─────────────────────────────────
  const getActiveAnnouncement = (country) =>
    axios.get(`${API}/api/announcements/active`, { params: country ? { country } : {} }).then(r => r.data);
  const listAnnouncements = () =>
    axios.get(`${API}/api/announcements`).then(r => r.data.announcements);
  const createAnnouncement = (payload) =>
    axios.post(`${API}/api/announcements`, payload).then(r => r.data.announcement);
  const updateAnnouncement = (id, payload) =>
    axios.patch(`${API}/api/announcements/${id}`, payload).then(r => r.data.announcement);

  const generateInvite = (email) => axios.post(`${API}/api/auth/invite/generate`, { email }).then(r => r.data);

  const listWaitlist = () => axios.get(`${API}/api/auth/waitlist`).then(r => r.data);

  const sendWaitlistInvite = (email) => axios.post(`${API}/api/auth/invite/send-waitlist`, { email }).then(r => r.data);

  const listMyInvites = () => axios.get(`${API}/api/auth/invites/mine`).then(r => r.data);

  const listAllInvites = () => axios.get(`${API}/api/auth/invites/all`).then(r => r.data);

  // Panel de admin · histórico de correos con su estado de entrega.
  const listEmailLogs = (params = {}) =>
    axios.get(`${API}/api/mail/logs`, { params }).then(r => r.data);

  // ── Collections (carpetas) ───────────────────────────────
  const listCollections = (userId) =>
    axios.get(`${API}/api/collections`, { params: userId ? { user: userId } : {} }).then(r => r.data.collections);
  const createCollection = (payload) =>
    axios.post(`${API}/api/collections`, payload).then(r => r.data.collection);
  const updateCollection = (id, payload) =>
    axios.patch(`${API}/api/collections/${id}`, payload).then(r => r.data.collection);
  const deleteCollection = (id) =>
    axios.delete(`${API}/api/collections/${id}`).then(r => r.data);
  const setPublicationCollections = (pubId, collectionIds) =>
    axios.put(`${API}/api/publications/${pubId}/collections`, { collections: collectionIds }).then(r => r.data.publication);

  // ── Publications (CRUD por owner) ────────────────────────
  const getPublication = (id) =>
    axios.get(`${API}/api/publications/${id}`).then(r => r.data.publication);
  const updatePublication = (id, formData) =>
    axios.patch(`${API}/api/publications/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }).then(r => r.data.publication);
  const deletePublication = (id) =>
    axios.delete(`${API}/api/publications/${id}`).then(r => r.data);

  // ── Seller role workflow ─────────────────────────────────
  const requestSellerRole = async () => {
    const { data } = await axios.post(`${API}/api/users/me/seller-request`);
    setUser(data.user);
    return data.user;
  };
  const listSellerRequests = (status = 'pending') =>
    axios.get(`${API}/api/users/admin/seller-requests`, { params: { status } }).then(r => r.data.users);
  const approveSellerRequest = (id) =>
    axios.post(`${API}/api/users/admin/seller-requests/${id}/approve`).then(r => r.data.user);
  const rejectSellerRequest = (id) =>
    axios.post(`${API}/api/users/admin/seller-requests/${id}/reject`).then(r => r.data.user);
  const listAllUsers = () =>
    axios.get(`${API}/api/users/admin/users`).then(r => r.data.users);
  const grantUserInvites = (id, count = 30) =>
    axios.post(`${API}/api/users/admin/users/${id}/grant-invites`, { count }).then(r => r.data);
  const getAdminMetrics = () =>
    axios.get(`${API}/api/users/admin/metrics`).then(r => r.data);

  // ── Follows (seguir vendedores) ──────────────────────────
  const getFollowStatus = (userId) =>
    axios.get(`${API}/api/follows/${userId}/status`).then(r => r.data);
  const followSeller = (userId) =>
    axios.post(`${API}/api/follows/${userId}`).then(r => r.data);
  const unfollowSeller = (userId) =>
    axios.delete(`${API}/api/follows/${userId}`).then(r => r.data);

  // ── Mensajes directos (DMs) ──────────────────────────────
  const listConversations = () =>
    axios.get(`${API}/api/messages/conversations`).then(r => r.data.conversations);
  const getConversation = (id, page = 1) =>
    axios.get(`${API}/api/messages/conversations/${id}`, { params: { page } }).then(r => r.data);
  const sendMessage = (id, text) =>
    axios.post(`${API}/api/messages/conversations/${id}`, { text }).then(r => r.data.message);
  const startConversation = (recipientId, text) =>
    axios.post(`${API}/api/messages/conversations/new`, { recipientId, text }).then(r => r.data);
  const markConversationRead = (id) =>
    axios.patch(`${API}/api/messages/conversations/${id}/read`).then(r => r.data);
  const getUnreadCount = () =>
    axios.get(`${API}/api/messages/unread-count`).then(r => r.data.count);

  // ── Billing (Stripe · Troly Premium) ─────────────────────
  // Crea la Checkout Session y devuelve la URL hosted de Stripe; el caller
  // redirige con window.location.assign(url).
  const createPremiumCheckout = () =>
    axios.post(`${API}/api/billing/checkout`).then(r => r.data);
  // Al volver del checkout: confirma el pago y refresca el user (ya premium).
  const confirmPremiumCheckout = async (sessionId) => {
    const { data } = await axios.get(`${API}/api/billing/confirm`, { params: { session_id: sessionId } });
    if (data.user) setUser(data.user);
    return data.user;
  };
  // Portal de facturación de Stripe (gestionar/cancelar suscripción).
  const openBillingPortal = () =>
    axios.post(`${API}/api/billing/portal`).then(r => r.data);

  // ── Orders ────────────────────────────────────────────────
  const createOrder = (payload) =>
    axios.post(`${API}/api/orders`, payload).then(r => r.data.order);
  const listMyOrders = () =>
    axios.get(`${API}/api/orders/mine`).then(r => r.data.orders);
  const listMySales = () =>
    axios.get(`${API}/api/orders/sales`).then(r => r.data);
  const updateOrderStatus = (id, status) =>
    axios.patch(`${API}/api/orders/${id}`, { status }).then(r => r.data.order);

  // ── Marketplace (Stripe Connect) ──────────────────────────
  const startConnectOnboarding = () =>
    axios.post(`${API}/api/marketplace/onboard`).then(r => r.data);
  const getConnectStatus = () =>
    axios.get(`${API}/api/marketplace/status`).then(r => r.data);
  const openConnectDashboard = () =>
    axios.post(`${API}/api/marketplace/dashboard`).then(r => r.data);
  const listPayableSellers = (ids) =>
    axios.get(`${API}/api/marketplace/sellers`, { params: { ids: ids.join(',') } }).then(r => r.data.payable);
  const createMarketplaceCheckout = (payload) =>
    axios.post(`${API}/api/marketplace/checkout`, payload).then(r => r.data);
  const confirmMarketplaceCheckout = (sessionId) =>
    axios.get(`${API}/api/marketplace/confirm`, { params: { session_id: sessionId } }).then(r => r.data);

  return (
    <Ctx.Provider value={{
      user, loading, refreshUser: fetchMe, login, register, facebookLogin, logout, updateMe, uploadAvatar,
      changePassword, changeEmail, deleteAccount, joinWaitlist, validateCode,
      validateInviteCode, startTrial, getReferralStatus, sendReferralInvite,
      getActiveAnnouncement, listAnnouncements, createAnnouncement, updateAnnouncement,
      generateInvite, listWaitlist, sendWaitlistInvite, listMyInvites, listAllInvites, listEmailLogs,
      listCollections, createCollection, updateCollection, deleteCollection, setPublicationCollections,
      getPublication, updatePublication, deletePublication,
      requestSellerRole, listSellerRequests, approveSellerRequest, rejectSellerRequest,
      listAllUsers, grantUserInvites, getAdminMetrics,
      createPremiumCheckout, confirmPremiumCheckout, openBillingPortal,
      createOrder, listMyOrders, listMySales, updateOrderStatus,
      startConnectOnboarding, getConnectStatus, openConnectDashboard,
      listPayableSellers, createMarketplaceCheckout, confirmMarketplaceCheckout,
      getFollowStatus, followSeller, unfollowSeller,
      listConversations, getConversation, sendMessage, startConversation,
      markConversationRead, getUnreadCount,
    }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
